import {
  createTotpMfaMethod,
  CreateTotpMfaMethodErrors,
  verifyMfaTotpMethod,
  VerifyMfaTotpMethodErrors,
} from "@/api/openapi";
import { PspContext } from "@/config/contexts";
import { translate } from "@/config/translator";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { MfaSetupContext } from "../../useMfaSetup";
import { useIsTablet } from "@/config/hooks";

export type AuthenticatorData = {
  methodId: string;
  uri: string;
  secret: string;
};

type UseMfaScanAuthenticatorQrParams = {
  onNext: () => void;
  setMfaSetupContext: Dispatch<SetStateAction<MfaSetupContext>>;
};

export function useMfaScanAuthenticatorQr({
  onNext,
  setMfaSetupContext,
}: UseMfaScanAuthenticatorQrParams) {
  const { lang } = useContext(PspContext);
  const isTablet = useIsTablet();

  const [authenticatorData, setAuthenticatorData] =
    useState<AuthenticatorData>();

  const [showCopyCheckmark, setShowCopyCheckmark] = useState(false);
  const hideCopyCheckmarkTimeoutRef = useRef<NodeJS.Timeout>(null);

  const [totpCode, setTotpCode] = useState("");
  const [showWrongTotpCodeWarning, setShowWrongTotpCodeWarning] =
    useState(false);

  useEffect(() => {
    createTotpMfaMethod({
      body: {
        mfaType: "totp",
      },
    }).then(({ data, error, status }) => {
      if (error) {
        if (status === 401) {
          const { detail } = error as CreateTotpMfaMethodErrors[typeof status];
          if (detail === "fresh_token_required") {
            toast(translate("yourTokenIsNotFresh", lang));
          } else {
            toast(translate("sessionTimedOut", lang));
          }
        }
        toast(translate("somethingWentWrong", lang));
      } else {
        setAuthenticatorData({
          methodId: data.id,
          uri: data.otpauthUrl,
          secret: data.secret,
        });
      }
    });
  }, [lang]);

  function onCopySecret() {
    if (hideCopyCheckmarkTimeoutRef.current)
      clearTimeout(hideCopyCheckmarkTimeoutRef.current);

    if (authenticatorData) {
      navigator.clipboard.writeText(authenticatorData.secret);
      setShowCopyCheckmark(true);
    } else {
      toast(translate("couldntCopySecret", lang));
    }

    hideCopyCheckmarkTimeoutRef.current = setTimeout(() => {
      setShowCopyCheckmark(false);
    }, 2000);
  }

  function onNextWrapper() {
    if (authenticatorData) {
      verifyMfaTotpMethod({
        body: { code: totpCode.replace(" ", "") },
        path: { mfa_method_id: authenticatorData.methodId },
      }).then(({ error, data, status }) => {
        if (error) {
          if (status === 401) {
            const { detail } =
              error as VerifyMfaTotpMethodErrors[typeof status];
            if (detail === "fresh_token_required") {
              toast(translate("yourTokenIsNotFresh", lang));
            } else {
              toast(translate("sessionTimedOut", lang));
            }
          } else if (status === 403) {
            setShowWrongTotpCodeWarning(true);
          } else {
            toast(translate("somethingWentWrong", lang));
          }
        } else {
          setMfaSetupContext((prev) => ({ ...prev, newMethod: data.method }));
          onNext();
        }
      });
    }
  }

  function onChangeTotp(value: string) {
    setTotpCode(value);
    setShowWrongTotpCodeWarning(false);
  }

  return {
    lang,
    authenticatorData,
    showCopyCheckmark,
    onCopySecret,
    totpCode,
    onNextWrapper,
    isTablet,
    showWrongTotpCodeWarning,
    onChangeTotp,
  };
}
